import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Loader2, Play, X } from "lucide-react";
import { PianoKeyboard } from "@/components/medusa/PianoKeyboard";
import { grantXP, saveProgress } from "@/components/medusa/player";

// Guided lesson: play the shown sequence in time with the metronome.
export function LessonRunner({ lesson, engine, onClose, onComplete }) {
  const [step, setStep] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [petrified, setPetrified] = useState([]);
  const [running, setRunning] = useState(false);
  const [timingErrs, setTimingErrs] = useState([]);
  const [done, setDone] = useState(null);
  const lastBeat = useRef(0);
  const beatMs = 60000 / lesson.tempo;

  const click = useCallback(() => {
    const ctx = engine?.current?.ensureCtx?.();
    if (!ctx) return;
    const o = ctx.createOscillator();
    o.type = "sine"; o.frequency.value = 1320;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.12, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.08);
  }, [engine]);

  useEffect(() => {
    if (!running || done) return;
    lastBeat.current = performance.now();
    const t = setInterval(() => { click(); lastBeat.current = performance.now(); }, beatMs);
    return () => clearInterval(t);
  }, [running, done, beatMs, click]);

  const finish = useCallback(async (finalMistakes, errs) => {
    const total = lesson.keys.length;
    const orderAcc = Math.max(0, (total - finalMistakes) / total) * 100;
    const avgTiming = errs.length ? errs.reduce((a, b) => a + b, 0) / errs.length : beatMs / 2;
    const timingAcc = Math.max(0, 100 - (avgTiming / beatMs) * 100);
    const accuracy = Math.round(orderAcc * 0.6 + timingAcc * 0.4);
    const xp = Math.round(lesson.xp * (accuracy / 100));
    setDone({ accuracy, xp });
    const res = await grantXP("piano", xp, `Lesson complete: ${lesson.title}`, lesson.lesson_id);
    await saveProgress("lesson", lesson.lesson_id, accuracy, accuracy);
    onComplete?.({ accuracy, xp, result: res });
  }, [lesson, beatMs, onComplete]);

  const handlePlay = (index) => {
    if (!running || done) return;
    const expected = lesson.keys[step];
    if (index === expected) {
      const err = Math.abs(performance.now() - lastBeat.current);
      const nextErrs = [...timingErrs, Math.min(err, beatMs - err < err ? beatMs - err : err)];
      setTimingErrs(nextErrs);
      if (step + 1 >= lesson.keys.length) {
        setRunning(false);
        finish(mistakes, nextErrs);
      } else {
        setStep(step + 1);
      }
    } else {
      setMistakes((m) => m + 1);
      setPetrified((p) => [...p, index]);
      setTimeout(() => setPetrified((p) => p.filter((k) => k !== index)), 1200);
    }
  };

  return (
    <div className="rounded-2xl border border-purple-500/30 bg-black/60 p-5 sm:p-6" data-testid="lesson-runner">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-purple-300">Lesson {lesson.order} · {lesson.tempo} BPM</p>
          <p className="font-display text-white font-bold">{lesson.title}</p>
        </div>
        <button onClick={onClose} aria-label="Close lesson" className="text-ice/40 hover:text-white transition-colors" data-testid="lesson-close"><X size={18} /></button>
      </div>

      {/* target sequence */}
      <div className="flex flex-wrap gap-1.5 mb-5" data-testid="lesson-sequence">
        {lesson.keys.map((k, i) => (
          <span key={i} className={`w-8 h-8 rounded-lg flex items-center justify-center font-mono text-[10px] border transition-all ${
            i < step ? "border-emerald-400/60 text-emerald-300 bg-emerald-500/10"
            : i === step ? "border-yellow-400 text-yellow-200 bg-yellow-500/15 shadow-[0_0_12px_rgba(255,184,0,0.4)]"
            : "border-[#2a2a34] text-ice/40"}`}
            data-testid={`lesson-note-${i}`}>
            {i < step ? <Check size={12} /> : k}
          </span>
        ))}
      </div>

      {!running && !done && (
        <button onClick={() => { setStep(0); setMistakes(0); setTimingErrs([]); setRunning(true); }}
          className="mb-4 inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white font-bold text-xs px-6 py-3 rounded-full active:scale-95 transition-all"
          data-testid="lesson-start">
          <Play size={13} /> Start the Metronome
        </button>
      )}
      {running && (
        <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.15em] text-ice/50">
          Strike key <span className="text-yellow-300">{lesson.keys[step]}</span> on each pulse · note {step + 1}/{lesson.keys.length} · mistakes {mistakes}
        </p>
      )}

      <PianoKeyboard engine={engine} onPlay={handlePlay} petrifiedKeys={petrified} disabled={!running || !!done} />

      {done && (
        <div className="mt-5 rounded-xl border border-emerald-400/40 bg-emerald-950/20 p-4 text-center" data-testid="lesson-result">
          <p className="font-display text-white font-bold text-lg">Accuracy {done.accuracy}%</p>
          <p className="text-xs text-emerald-300 mt-1">+{done.xp} XP earned{done.accuracy >= 90 ? " — flawless technique." : done.accuracy >= 70 ? " — solid. Repeat to sharpen." : " — run it back; slow is smooth, smooth is fast."}</p>
        </div>
      )}
    </div>
  );
}
